/**
 * TrayLayer — 3D ladder-style cable trays for the electrical-room scene.
 * Extracted from `scene.js` (iter 42) to bring that file under the
 * 700-line refactor threshold and mirror the `conduits.js` pattern.
 *
 * Renders three tray kinds:
 *   · run     → horizontal ladder (two parallel rails + perpendicular rungs)
 *   · drop    → vertical ladder hugging the nearest wall
 *   · fitting → smart-join elbow/tee/cross with curved rails (quadratic
 *               Bezier) or legacy torus marker for un-joined fittings.
 *
 * CRITICAL: Everything constructed with `React.createElement` (the `h`
 * alias) — NOT JSX. The visual-edits babel plugin injects line-number
 * props that Three.js objects refuse to consume.
 */
import React, { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { Edges } from "@react-three/drei";
import * as THREE from "three";

import { IN_TO_M } from "./scene";

const h = React.createElement;

/* ─── AnimatedRotate ──────────────────────────────────────────────
   Tiny <group> wrapper that smoothly lerps its X/Y rotation from its
   current value to the target over ~200 ms. Used by the tray builders
   so toggling rotation_axis_deg animates instead of snaps. */
function AnimatedRotate({ targetX = 0, targetY = 0, children }) {
  const ref = useRef();
  useFrame((_, dt) => {
    if (!ref.current) return;
    const k = 1 - Math.exp(-dt * 16);
    ref.current.rotation.x += (targetX - ref.current.rotation.x) * k;
    ref.current.rotation.y += (targetY - ref.current.rotation.y) * k;
  });
  return h("group", { ref }, children);
}

export default function TrayLayer({ project, trayDefaults, selectedTrayId, setSelectedTrayId, exploded, fillBySegment, onTrayContextMenu }) {
  const segs = project.cable_trays || [];
  if (segs.length === 0) return null;
  const room_H_in = project.depth_in || 96;
  const tray_w_in   = trayDefaults?.width_in  || 12;
  const rail_w_in   = 1.0;
  const rung_w_in   = 0.5;
  const rail_h_in   = trayDefaults?.height_in || 4;
  const rungPitchIn = 9;
  const ceilingMountIn = room_H_in - 12;
  const dropTopIn = ceilingMountIn;
  const dropBottomIn = 78;

  const rungMat = () => h("meshStandardMaterial", {
    color: "#00B6CC", transparent: true, opacity: 0.6,
    metalness: 0.4, roughness: 0.5,
  });

  // Selection no longer changes the tray's footprint (was confusing — width
  // appeared to swell). We highlight with color/emissive only.
  const explodeOffset_in = 0;
  const rungLift_in      = 0;

  /** NEC 392.22 fill-pct → green/amber/red. */
  const fillColor = (pct) => {
    if (pct == null) return null;
    if (pct > 50) return "#FF3B5C";      // red — over fill limit
    if (pct > 30) return "#FFCC00";      // amber — getting full
    return "#22c55e";                     // green — healthy
  };

  const cableMatColors = ["#FFCC00", "#00E5FF", "#FF8A85", "#a3e635", "#c084fc"];

  const buildRun = (s) => {
    const isSelected = selectedTrayId === s.id;
    const [[x1, y1], [x2, y2]] = s.points;
    const dx = x2 - x1, dy = y2 - y1;
    const len_in = Math.hypot(dx, dy);
    if (len_in < 0.1) return null;
    const cx = ((x1 + x2) / 2) * IN_TO_M;
    const cz = ((y1 + y2) / 2) * IN_TO_M;
    // Per-tray elevation override (defaults to ceiling - 12").
    const runElevationIn = (s.elevation_in != null) ? s.elevation_in : ceilingMountIn;
    const cy = runElevationIn * IN_TO_M;
    const angle = Math.atan2(dy, dx);

    const railOffset = ((tray_w_in / 2) + (isSelected ? explodeOffset_in : 0)) * IN_TO_M;
    const liftY = isSelected ? rungLift_in * IN_TO_M : 0;

    const onClick = (e) => { e.stopPropagation(); setSelectedTrayId(isSelected ? null : s.id); };
    const onContextMenu = (e) => {
      e.stopPropagation();
      // r3f forwards the native pointer event under e.nativeEvent. Suppress
      // the browser menu and bubble up to Room3DView.
      const native = e.nativeEvent || e;
      native.preventDefault && native.preventDefault();
      onTrayContextMenu && onTrayContextMenu({
        x: native.clientX || 0, y: native.clientY || 0, seg: s,
      });
    };

    const fillPct = fillBySegment?.[s.id];
    const fc = fillColor(fillPct);

    const rails = [-1, 1].map((dir) =>
      h("mesh", {
        key: `rail-${s.id}-${dir}`,
        position: [0, 0, dir * railOffset],
        onClick,
        onContextMenu,
        onPointerOver: (e) => { e.stopPropagation(); document.body.style.cursor = "pointer"; },
        onPointerOut: () => { document.body.style.cursor = "auto"; },
      },
        h("boxGeometry", { args: [len_in * IN_TO_M, rail_h_in * IN_TO_M, rail_w_in * IN_TO_M] }),
        h("meshStandardMaterial", {
          color: isSelected ? "#FFCC00" : (fc || "#00E5FF"),
          transparent: true, opacity: isSelected ? 0.85 : 0.55,
          emissive: isSelected ? "#FFCC00" : (fc || "#00E5FF"),
          emissiveIntensity: isSelected ? 0.35 : 0.18,
          metalness: 0.45, roughness: 0.45,
        }),
        h(Edges, { color: isSelected ? "#FFCC00" : (fc || "#00E5FF"), threshold: 1 }),
      ),
    );
    const nRungs = Math.max(0, Math.floor(len_in / rungPitchIn));
    const rungs = [];
    for (let i = 1; i < nRungs; i++) {
      const tLocal_in = i * rungPitchIn - len_in / 2;
      rungs.push(
        h("mesh", { key: `rung-${s.id}-${i}`, position: [tLocal_in * IN_TO_M, liftY, 0], onClick, onContextMenu },
          h("boxGeometry", { args: [rung_w_in * IN_TO_M, rail_h_in * 0.6 * IN_TO_M,
                                    (tray_w_in + (isSelected ? explodeOffset_in * 2 : 0)) * IN_TO_M] }),
          rungMat(),
        ),
      );
    }
    // Cables inside the tray — visible when exploded so the user can see the fill.
    const cables = isSelected ? cableMatColors.map((color, idx) => {
      const yOff = (-1 + (idx + 1) / (cableMatColors.length + 1) * 2) * (tray_w_in / 2 * 0.7) * IN_TO_M;
      return h("mesh", { key: `cable-${s.id}-${idx}`, position: [0, -rail_h_in * 0.1 * IN_TO_M, yOff] },
        h("cylinderGeometry", { args: [0.6 * IN_TO_M, 0.6 * IN_TO_M, len_in * IN_TO_M, 12] }),
        h("meshStandardMaterial", { color, metalness: 0.1, roughness: 0.8 }),
      );
    }) : [];
    // Cables are along run axis → rotate the cylinders so they lie horizontal.
    const cablesRotated = cables.map((c, i) => h("group", { key: `cgrp-${s.id}-${i}`, rotation: [0, 0, Math.PI / 2] }, c));

    // Optional 90° rotation about the run's own centerline (iter_29):
    // flips rails from a horizontal pair (left/right) to a vertical pair
    // (top/bottom). The wide face then faces the ceiling instead of the wall.
    const rotXLocal = (Number(s.rotation_axis_deg) === 90) ? Math.PI / 2 : 0;

    return h("group", { key: s.id, position: [cx, cy, cz], rotation: [0, -angle, 0] },
      h(AnimatedRotate, { targetX: rotXLocal },
        ...rails, ...rungs, ...cablesRotated,
      ),
    );
  };

  const buildDrop = (s) => {
    const isSelected = selectedTrayId === s.id;
    const [x1, y1] = s.points[0];
    // Per-tray top/bottom overrides (defaults: ceiling-mount → 78").
    const topIn    = (s.top_in    != null) ? s.top_in    : dropTopIn;
    const bottomIn = (s.bottom_in != null) ? s.bottom_in : dropBottomIn;
    const dropLen_in = Math.max(6, topIn - bottomIn);
    const cx = x1 * IN_TO_M;
    const cz = y1 * IN_TO_M;
    const cy = ((topIn + bottomIn) / 2) * IN_TO_M;
    const onClick = (e) => { e.stopPropagation(); setSelectedTrayId(isSelected ? null : s.id); };
    const onContextMenu = (e) => {
      e.stopPropagation();
      const native = e.nativeEvent || e;
      native.preventDefault && native.preventDefault();
      onTrayContextMenu && onTrayContextMenu({
        x: native.clientX || 0, y: native.clientY || 0, seg: s,
      });
    };
    const fillPct = fillBySegment?.[s.id];
    const fc = fillColor(fillPct);

    // ── Wide-side-flat-on-wall orientation ───────────────────────────
    // We align the tray's WIDTH (rail-to-rail span) parallel to the nearest
    // wall, so the wide face presents against the wall. Rails are full
    // side-walls (height = rail_h_in, the C-channel depth) that stick out
    // perpendicular to the wall — matching the horizontal-run geometry.
    const room_L = project.length_in || 240;
    const room_W = project.width_in  || 180;
    const dToLeft  = x1;
    const dToRight = room_L - x1;
    const dToFront = y1;
    const dToBack  = room_W - y1;
    const minDist  = Math.min(dToLeft, dToRight, dToFront, dToBack);
    const wallAxisIsX = (minDist === dToLeft || minDist === dToRight);
    // Optional 90° rotation about the drop's own vertical axis (iter_29) —
    // applied as an animated Y-rotation in the AnimatedRotate wrapper below.
    const rotYLocal = (Number(s.rotation_axis_deg) === 90) ? Math.PI / 2 : 0;

    // wallAxisIsX (left/right wall, normal = X):
    //   width axis = Z (along the wall), depth axis = X (into the room)
    //   → rail separation on Z; rail box: [depth, length, thickness]
    //                                    = [rail_h, dropLen, rail_w]
    //   → rungs span Z by tray_w_in
    // wallAxisIsZ (front/back wall, normal = Z):
    //   width axis = X, depth axis = Z
    //   → rail separation on X; rail box: [rail_w, dropLen, rail_h]
    //   → rungs span X by tray_w_in
    const railBox = wallAxisIsX
      ? [rail_h_in, dropLen_in, rail_w_in]      // depth=X, length=Y, thickness=Z
      : [rail_w_in, dropLen_in, rail_h_in];     // thickness=X, length=Y, depth=Z
    const railSep = wallAxisIsX ? [0, 0, 1] : [1, 0, 0];
    // Position each rail at the EDGE of the tray width (half-width offset)
    // along the rail-separation axis.
    const halfWidth = (tray_w_in / 2 + (isSelected ? explodeOffset_in : 0)) * IN_TO_M;
    const rungSize = wallAxisIsX
      ? [rail_w_in, rung_w_in, tray_w_in + (isSelected ? explodeOffset_in * 2 : 0)]
      : [tray_w_in + (isSelected ? explodeOffset_in * 2 : 0), rung_w_in, rail_w_in];

    const rails = [-1, 1].map((dir) =>
      h("mesh", {
        key: `drop-rail-${s.id}-${dir}`,
        position: [dir * halfWidth * railSep[0], 0, dir * halfWidth * railSep[2]],
        onClick,
        onContextMenu,
      },
        h("boxGeometry", { args: [railBox[0] * IN_TO_M, railBox[1] * IN_TO_M, railBox[2] * IN_TO_M] }),
        h("meshStandardMaterial", {
          color: isSelected ? "#FFCC00" : (fc || "#00E5FF"),
          transparent: true, opacity: isSelected ? 0.85 : 0.55,
          emissive: isSelected ? "#FFCC00" : (fc || "#00E5FF"),
          emissiveIntensity: isSelected ? 0.35 : 0.18,
          metalness: 0.45, roughness: 0.45,
        }),
        h(Edges, { color: isSelected ? "#FFCC00" : (fc || "#00E5FF"), threshold: 1 }),
      ),
    );
    const nRungs = Math.max(0, Math.floor(dropLen_in / rungPitchIn));
    const rungs = [];
    for (let i = 1; i < nRungs; i++) {
      const yLocal_in = i * rungPitchIn - dropLen_in / 2;
      rungs.push(
        h("mesh", { key: `drop-rung-${s.id}-${i}`, position: [0, yLocal_in * IN_TO_M, 0], onClick, onContextMenu },
          h("boxGeometry", { args: [
            rungSize[0] * IN_TO_M,
            rungSize[1] * IN_TO_M,
            rungSize[2] * IN_TO_M,
          ] }),
          rungMat(),
        ),
      );
    }
    return h("group", { key: s.id, position: [cx, cy, cz] },
      h(AnimatedRotate, { targetY: rotYLocal }, ...rails, ...rungs),
    );
  };

  /* ─── Fitting (elbow / reducer / tee) ─────────────────────────────
     Smart-join fittings store `joint_meta = {a_leg_pt:[x,y], b_leg_pt:[x,y],
     angle_deg}`. We render those as a curved tube going a_leg → corner →
     b_leg using a quadratic Bezier with the corner as the control point —
     visually matches a real elbow / tee. Legacy single-point fittings (no
     joint_meta) keep the simple yellow torus marker at the drop point so
     they remain visible. */
  const buildFitting = (s) => {
    const isSelected = selectedTrayId === s.id;
    const onClick = (e) => { e.stopPropagation(); setSelectedTrayId(isSelected ? null : s.id); };
    const onContextMenu = (e) => {
      e.stopPropagation();
      const native = e.nativeEvent || e;
      native.preventDefault && native.preventDefault();
      onTrayContextMenu && onTrayContextMenu({
        x: native.clientX || 0, y: native.clientY || 0, seg: s,
      });
    };
    const fittingColor = isSelected ? "#FFCC00" : "#FFD24A";
    const [fx, fy] = s.points[0] || [0, 0];
    const elevationIn = (s.elevation_in != null) ? s.elevation_in : ceilingMountIn;
    const cx_m = fx * IN_TO_M;
    const cz_m = fy * IN_TO_M;
    const cy_m = elevationIn * IN_TO_M;

    const meta = s.joint_meta;
    // Legacy fittings — show a horizontal torus marker so they remain visible.
    if (!meta || (!meta.a_leg_pt && !meta.leg_points)) {
      return h("group", { key: s.id, position: [cx_m, cy_m, cz_m], onClick, onContextMenu },
        h("mesh", { rotation: [Math.PI / 2, 0, 0] },
          h("torusGeometry", { args: [4 * IN_TO_M, 1.2 * IN_TO_M, 10, 24] }),
          h("meshStandardMaterial", {
            color: fittingColor, emissive: fittingColor,
            emissiveIntensity: isSelected ? 0.5 : 0.3,
            transparent: true, opacity: 0.85,
            metalness: 0.55, roughness: 0.4,
          }),
        ),
      );
    }

    // Multi-tray (tee / cross) — `leg_points` is an array of 3-4 leg endpoints.
    // Render a short straight tube from each leg back to the corner plus a
    // junction sphere at the meeting point.
    if (meta.leg_points && meta.leg_points.length >= 3) {
      const tubeRadiusM = ((s.width_in || tray_w_in) / 2) * 0.9 * IN_TO_M;
      const cornerV = new THREE.Vector3(cx_m, cy_m, cz_m);
      const stubs = meta.leg_points.map((lp, i) => {
        const start = new THREE.Vector3(lp[0] * IN_TO_M, cy_m, lp[1] * IN_TO_M);
        const curve = new THREE.LineCurve3(start, cornerV);
        return h("mesh", { key: `mleg-${s.id}-${i}` },
          h("tubeGeometry", { args: [curve, 1, tubeRadiusM, 12, false] }),
          h("meshStandardMaterial", {
            color: fittingColor, emissive: fittingColor,
            emissiveIntensity: isSelected ? 0.45 : 0.22,
            transparent: true, opacity: isSelected ? 0.92 : 0.72,
            metalness: 0.55, roughness: 0.4,
          }),
        );
      });
      // Ring cap at each leg start
      const caps = meta.leg_points.map((lp, i) =>
        h("mesh", {
          key: `mcap-${s.id}-${i}`,
          position: [lp[0] * IN_TO_M, cy_m, lp[1] * IN_TO_M],
          rotation: [Math.PI / 2, 0, 0],
        },
          h("torusGeometry", { args: [tubeRadiusM * 1.05, rail_w_in * IN_TO_M * 0.7, 6, 18] }),
          h("meshStandardMaterial", { color: fittingColor, emissive: fittingColor,
                                        emissiveIntensity: 0.3, transparent: true, opacity: 0.85 }),
        ),
      );
      // Junction sphere
      const junction = h("mesh", { key: `mjunc-${s.id}`, position: [cx_m, cy_m, cz_m] },
        h("sphereGeometry", { args: [tubeRadiusM * 1.3, 16, 12] }),
        h("meshStandardMaterial", { color: fittingColor, emissive: fittingColor,
                                      emissiveIntensity: isSelected ? 0.5 : 0.3,
                                      transparent: true, opacity: 0.9,
                                      metalness: 0.6, roughness: 0.35 }),
      );
      return h("group", { key: s.id, onClick, onContextMenu,
                            onPointerOver: (e) => { e.stopPropagation(); document.body.style.cursor = "pointer"; },
                            onPointerOut: () => { document.body.style.cursor = "auto"; } },
        ...stubs, ...caps, junction,
      );
    }

    // Smart-join fitting — quadratic Bezier curve through (a_leg, corner, b_leg)
    // gives a faithful elbow when ta⊥tb (90°) or a slow bend at 45°.
    const [ax, ay] = meta.a_leg_pt;
    const [bx, by] = meta.b_leg_pt;

    // Vertical bend (user opt-in): look up the joining segments to derive each
    // leg's true Y. Drops anchor at the drop's bottom_in (floor side); runs
    // anchor at the run's elevation_in (ceiling side). Without vertical_bend
    // both legs and the control point sit at the fitting's elevation so the
    // elbow stays in the horizontal plane (default behaviour).
    let yA = cy_m, yB = cy_m, yCorner = cy_m;
    if (meta.vertical_bend) {
      const segA = segs.find((x) => x.id === meta.seg_a_id);
      const segB = segs.find((x) => x.id === meta.seg_b_id);
      const yFor = (seg) => {
        if (!seg) return cy_m;
        if (seg.kind === "drop") {
          const b = (seg.bottom_in != null) ? seg.bottom_in : dropBottomIn;
          return b * IN_TO_M;
        }
        const e = (seg.elevation_in != null) ? seg.elevation_in : ceilingMountIn;
        return e * IN_TO_M;
      };
      yA = yFor(segA);
      yB = yFor(segB);
      // Place the control point at the elevation of the HIGHER leg so the
      // bend curves smoothly down/up rather than overshooting.
      yCorner = Math.max(yA, yB);
    }

    const start  = new THREE.Vector3(ax * IN_TO_M, yA, ay * IN_TO_M);
    const corner = new THREE.Vector3(cx_m,        yCorner, cz_m);
    const end    = new THREE.Vector3(bx * IN_TO_M, yB, by * IN_TO_M);
    const curve  = new THREE.QuadraticBezierCurve3(start, corner, end);

    // Sample N+1 points along the centerline, derive ladder rails by offsetting
    // ±halfWidth perpendicular to the tangent (horizontal plane). The rails meet
    // the tray's own rail edges exactly at start/end because at t=0 / t=1 the
    // curve tangent equals the joining tray's outward tangent.
    const halfW = ((s.width_in || tray_w_in) / 2) * IN_TO_M;
    const N = 28;
    const leftPts = [];
    const rightPts = [];
    for (let i = 0; i <= N; i++) {
      const t = i / N;
      const p = curve.getPoint(t);
      const tan = curve.getTangent(t).normalize();
      // perpendicular in horizontal plane (Y up): rotate tangent 90° around Y.
      const perp = new THREE.Vector3(-tan.z, 0, tan.x).normalize();
      leftPts .push(p.clone().add(perp.clone().multiplyScalar(halfW)));
      rightPts.push(p.clone().sub(perp.clone().multiplyScalar(halfW)));
    }
    const leftCurve  = new THREE.CatmullRomCurve3(leftPts);
    const rightCurve = new THREE.CatmullRomCurve3(rightPts);
    const railRadius = rail_w_in * IN_TO_M * 0.6;

    // Cross-rungs every 4 samples — short boxes spanning the two rails.
    const rungs = [];
    for (let i = 2; i < N; i += 4) {
      const lp = leftPts[i], rp = rightPts[i];
      const mid = new THREE.Vector3().addVectors(lp, rp).multiplyScalar(0.5);
      const len = lp.distanceTo(rp);
      // Rotate so the box's local +x lies along (rp − lp).
      const dir = new THREE.Vector3().subVectors(rp, lp).normalize();
      const yaw = Math.atan2(dir.x, dir.z);   // rotation around Y to face dir
      rungs.push(h("mesh", {
        key: `rung-${s.id}-${i}`,
        position: [mid.x, mid.y, mid.z],
        rotation: [0, yaw - Math.PI / 2, 0],
      },
        h("boxGeometry", { args: [len, rail_w_in * IN_TO_M * 0.8, rail_w_in * IN_TO_M * 0.8] }),
        h("meshStandardMaterial", { color: fittingColor, emissive: fittingColor,
                                      emissiveIntensity: isSelected ? 0.4 : 0.2,
                                      metalness: 0.6, roughness: 0.4 }),
      ));
    }

    return h("group", { key: s.id, onClick, onContextMenu,
                          onPointerOver: (e) => { e.stopPropagation(); document.body.style.cursor = "pointer"; },
                          onPointerOut: () => { document.body.style.cursor = "auto"; } },
      // Translucent inner sheath shows the cable envelope without dominating.
      h("mesh", null,
        h("tubeGeometry", { args: [curve, N, halfW * 0.95, 14, false] }),
        h("meshStandardMaterial", {
          color: fittingColor, emissive: fittingColor,
          emissiveIntensity: isSelected ? 0.35 : 0.12,
          transparent: true, opacity: isSelected ? 0.32 : 0.18,
          metalness: 0.4, roughness: 0.6,
        }),
      ),
      // Two ladder rails.
      h("mesh", { key: `lr-${s.id}` },
        h("tubeGeometry", { args: [leftCurve, N, railRadius, 10, false] }),
        h("meshStandardMaterial", { color: fittingColor, emissive: fittingColor,
                                      emissiveIntensity: isSelected ? 0.5 : 0.3,
                                      metalness: 0.6, roughness: 0.35 }),
      ),
      h("mesh", { key: `rr-${s.id}` },
        h("tubeGeometry", { args: [rightCurve, N, railRadius, 10, false] }),
        h("meshStandardMaterial", { color: fittingColor, emissive: fittingColor,
                                      emissiveIntensity: isSelected ? 0.5 : 0.3,
                                      metalness: 0.6, roughness: 0.35 }),
      ),
      // Cross-rungs along the bend.
      ...rungs,
    );
  };

  const meshes = segs.map((s) => {
    if (!s.points || s.points.length < 1) return null;
    if (s.kind === "drop")    return buildDrop(s);
    if (s.kind === "fitting") return buildFitting(s);
    if (s.points.length < 2)  return null;
    return buildRun(s);
  }).filter(Boolean);
  return h("group", null, ...meshes);
}
